import {useEffect, useEffectEvent, useId, useRef, useState, type ReactElement} from 'react'
import {
  ActionList,
  AnchoredOverlay,
  Button,
  IconButton,
  SkeletonBox,
  TextInput,
  Tooltip,
  VisuallyHidden,
} from '@primer/react'
import {announce} from '@github-ui/aria-live'
import {MessageCircleIcon} from '@github-ui/custom-icons/MessageCircleIcon'
import {
  ArrowLeftIcon,
  ChevronRightIcon,
  LockIcon,
  OrganizationIcon,
  PersonIcon,
  RepoIcon,
  ShieldIcon,
} from '@primer/octicons-react'
import {clsx} from 'clsx'
import type {AgentDropdownRepository} from '@github-ui/copilot-chat-types'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {isRepoInScope} from '../shared'
import type {ChatOwnerControl} from './chat-owner-control'
import {
  chatTargetRepositoryCacheKey,
  getCachedChatTargetRepositories,
  revalidateChatTargetRepositories,
  shouldRevalidateChatTargetRepositories,
} from './chat-target-repository-cache'
import {
  CHAT_DESCRIPTION,
  GENERAL_CONVERSATIONS_HEADING,
  SEARCH_REPOSITORIES_PLACEHOLDER,
  SELECT_REPOSITORY_TITLE,
  WORK_IN_REPOSITORY_HEADING,
} from './chat-target-copy'
import styles from './ChatTargetPicker.module.css'

const LOADING_ROWS = [0, 1, 2, 3]

export interface ChatTargetPickerProps {
  chatOwner: ChatOwnerControl
  selectedRepository?: AgentDropdownRepository
  chatSelected: boolean
  triggerText: string
  triggerLabel: string
  triggerClassName: string
  disabled?: boolean
  repositoryLoader: (query: string) => Promise<AgentDropdownRepository[]>
  includedRepositoryNameWithOwner?: string
  onLoad?: (repositories: AgentDropdownRepository[]) => void
  onError?: () => void
  onSelectRepository: (repository: AgentDropdownRepository) => void
  onSelectChat: () => void
  secondaryAction?: ReactElement
  scopeOwner?: string | null
  currentUserLogin?: string
  loadingElement: ReactElement
  showNotice?: boolean
}

export function ChatTargetPicker({
  chatOwner,
  selectedRepository,
  chatSelected,
  triggerText,
  triggerLabel,
  triggerClassName,
  disabled,
  repositoryLoader,
  includedRepositoryNameWithOwner,
  onLoad,
  onError,
  onSelectRepository,
  onSelectChat,
  secondaryAction,
  scopeOwner,
  currentUserLogin,
  loadingElement,
  showNotice = false,
}: ChatTargetPickerProps) {
  const [open, setOpen] = useState(Boolean(chatOwner.reopenRequested))
  const [ownerView, setOwnerView] = useState(false)
  const [filter, setFilter] = useState('')
  const query = filter.trim().toLowerCase()
  const cacheKey = chatTargetRepositoryCacheKey(scopeOwner, filter, includedRepositoryNameWithOwner)
  const cachedRepositories = getCachedChatTargetRepositories(cacheKey)
  const [loadState, setLoadState] = useState(() => ({
    key: cacheKey,
    repositories: [] as AgentDropdownRepository[],
    loadedOnce: false,
  }))
  const hasCurrentResults = Boolean(cachedRepositories) || (loadState.key === cacheKey && loadState.loadedOnce)
  const [errorKey, setErrorKey] = useState<string | null>(null)
  const [retryCount, setRetryCount] = useState(0)
  const loadFailed = !hasCurrentResults && errorKey === cacheKey
  const loading = !hasCurrentResults && !loadFailed
  const {reopenRequested, onReopenHandled} = chatOwner
  const searchRef = useRef<HTMLInputElement>(null)
  const anchorRef = useRef<HTMLButtonElement>(null)
  const ownerPanelRef = useRef<HTMLDivElement>(null)
  const backRef = useRef<HTMLButtonElement>(null)
  const disclosureRef = useRef<HTMLLIElement>(null)
  const listRef = useRef<HTMLUListElement>(null)
  const requestSequenceRef = useRef(0)
  const previousFilterRef = useRef(query)
  const previousAnnouncementRef = useRef<string | null>(null)
  const hasOpenedRef = useRef(Boolean(chatOwner.reopenRequested))
  const titleId = useId()
  const searchId = useId()

  const finishLoad = useEffectEvent((result: AgentDropdownRepository[], key: string) => {
    setLoadState({key, repositories: result, loadedOnce: true})
    setErrorKey(null)
    onLoad?.(result)
  })
  const reportCachedLoad = useEffectEvent((result: AgentDropdownRepository[]) => onLoad?.(result))
  const reportLoadFailure = useEffectEvent(() => onError?.())
  useEffect(() => {
    const request = ++requestSequenceRef.current
    const delay = previousFilterRef.current === query ? 0 : 200
    previousFilterRef.current = query
    const cached = getCachedChatTargetRepositories(cacheKey)
    if (cached && scopeOwner !== null && scopeOwner !== '') reportCachedLoad(cached.repositories)
    let timeout: ReturnType<typeof setTimeout> | undefined
    if (scopeOwner !== null && scopeOwner !== '' && shouldRevalidateChatTargetRepositories(cacheKey)) {
      timeout = setTimeout(() => {
        void (async () => {
          try {
            const pending = revalidateChatTargetRepositories(cacheKey, () => repositoryLoader(query))
            if (pending) {
              const result = await pending
              if (request === requestSequenceRef.current) finishLoad(result, cacheKey)
            }
          } catch {
            if (request === requestSequenceRef.current && !getCachedChatTargetRepositories(cacheKey)) {
              setErrorKey(cacheKey)
              if (!query.trim()) reportLoadFailure()
            }
          }
        })()
      }, delay)
    }
    return () => {
      requestSequenceRef.current = request + 1
      if (timeout) clearTimeout(timeout)
    }
  }, [cacheKey, query, repositoryLoader, scopeOwner, retryCount])

  const retryLoad = () => {
    setErrorKey(null)
    setRetryCount(count => count + 1)
  }

  useEffect(() => {
    if (!reopenRequested) return
    let focusFrame: number
    const frame = requestAnimationFrame(() => {
      const wasOpened = hasOpenedRef.current
      hasOpenedRef.current = true
      setOpen(true)
      setOwnerView(false)
      setFilter('')
      if (
        loadFailed ||
        (wasOpened && getCachedChatTargetRepositories(cacheKey) && shouldRevalidateChatTargetRepositories(cacheKey))
      ) {
        retryLoad()
      }
      focusFrame = requestAnimationFrame(() => {
        searchRef.current?.focus()
        onReopenHandled?.()
      })
    })
    return () => {
      cancelAnimationFrame(frame)
      cancelAnimationFrame(focusFrame)
    }
  }, [reopenRequested, onReopenHandled, loadFailed, cacheKey])

  const goBack = () => {
    setOwnerView(false)
    requestAnimationFrame(() => disclosureRef.current?.focus())
  }
  const close = () => {
    setOpen(false)
    setOwnerView(false)
    setFilter('')
  }
  const showOwner = () => {
    if (!chatOwner.renderPanel) return
    setOwnerView(true)
    requestAnimationFrame(() => {
      const input = ownerPanelRef.current?.querySelector('input')
      if (input) input.focus()
      else backRef.current?.focus()
    })
  }
  const noticeOwner =
    scopeOwner?.toLowerCase() === currentUserLogin?.toLowerCase() ? 'your personal account' : scopeOwner
  const notice = `Repositories are limited to ${noticeOwner}. Change the Chat scope to work in another owner's repositories.`
  const visibleRepositories = (
    loadState.key === cacheKey && loadState.loadedOnce
      ? loadState.repositories
      : (cachedRepositories?.repositories ?? [])
  ).filter(repository => isRepoInScope(repository, scopeOwner))
  const announcement = loadFailed
    ? 'Could not load repositories'
    : loading
      ? 'Searching repositories…'
      : visibleRepositories.length === 0
        ? 'No repositories found'
        : `${visibleRepositories.length} ${visibleRepositories.length === 1 ? 'repository' : 'repositories'}`
  useEffect(() => {
    if (!open || ownerView) {
      previousAnnouncementRef.current = null
      return
    }
    if (previousAnnouncementRef.current === announcement) return
    previousAnnouncementRef.current = announcement
    announce(announcement, {assertive: false})
  }, [announcement, open, ownerView])

  const firstEnabledItem = () =>
    listRef.current?.querySelector<HTMLElement>(
      '[role="menuitemradio"]:not([aria-disabled="true"]), [role="menuitem"]:not([aria-disabled="true"])',
    )
  const repositoryItems = visibleRepositories.map(repository => (
    <ActionList.Item
      key={repository.nameWithOwner}
      role="menuitemradio"
      aria-checked={selectedRepository?.nameWithOwner === repository.nameWithOwner}
      selected={selectedRepository?.nameWithOwner === repository.nameWithOwner}
      disabled={!repository.enabled}
      onSelect={() => {
        onSelectRepository(repository)
        close()
      }}
    >
      <ActionList.LeadingVisual>{repository.private ? <LockIcon /> : <RepoIcon />}</ActionList.LeadingVisual>
      {repository.nameWithOwner}
    </ActionList.Item>
  ))

  return (
    <AnchoredOverlay
      anchorRef={anchorRef}
      open={open}
      onOpen={() => {
        const wasOpened = hasOpenedRef.current
        hasOpenedRef.current = true
        setOpen(true)
        if (
          loadFailed ||
          (wasOpened && getCachedChatTargetRepositories(cacheKey) && shouldRevalidateChatTargetRepositories(cacheKey))
        ) {
          retryLoad()
        }
      }}
      onClose={gesture => {
        if (gesture === 'escape' && ownerView) goBack()
        else close()
      }}
      renderAnchor={anchorProps => {
        const trigger = (
          <Button
            {...anchorProps}
            type="button"
            aria-label={triggerLabel}
            aria-haspopup="dialog"
            variant="invisible"
            size="small"
            labelWrap
            disabled={disabled}
            className={clsx(triggerClassName, styles.Trigger)}
            leadingVisual={chatSelected ? MessageCircleIcon : undefined}
          >
            {!loadState.loadedOnce && loading && !selectedRepository && !chatSelected ? loadingElement : triggerText}
          </Button>
        )
        return chatSelected && !disabled ? (
          <Tooltip text={chatOwner.accessibleLabel} type="description" direction="n" _privateDisableTooltip={open}>
            {trigger}
          </Tooltip>
        ) : (
          trigger
        )
      }}
      side="outside-top"
      width="medium"
      focusTrapSettings={{initialFocusRef: searchRef}}
      focusZoneSettings={{disabled: true}}
      overlayProps={{
        role: 'dialog',
        maxHeight: 'large',
        className: styles.Overlay,
        ...(ownerView ? {'aria-label': 'Session scope'} : {'aria-labelledby': titleId}),
      }}
    >
      <div className={styles.OverlayContent}>
        <div ref={ownerPanelRef} className={styles.Content} hidden={!ownerView}>
          <div className={styles.BackRow}>
            <IconButton
              ref={backRef}
              icon={ArrowLeftIcon}
              aria-label="Back to repositories"
              variant="invisible"
              onClick={goBack}
            />
          </div>
          {ownerView && <div className={styles.OwnerPanel}>{chatOwner.renderPanel?.()}</div>}
        </div>
        <div className={styles.Content} hidden={ownerView}>
          <div className={styles.Heading}>
            <span id={titleId}>{SELECT_REPOSITORY_TITLE}</span>
            {showNotice && typeof scopeOwner === 'string' && scopeOwner !== '' && (
              <Tooltip text={notice} direction="e">
                <IconButton
                  icon={ShieldIcon}
                  aria-label="Repository selection information"
                  variant="invisible"
                  size="small"
                />
              </Tooltip>
            )}
          </div>
          <label htmlFor={searchId} className={styles.SearchLabel}>
            Search repositories
          </label>
          <TextInput
            id={searchId}
            ref={searchRef}
            placeholder={SEARCH_REPOSITORIES_PLACEHOLDER}
            value={filter}
            onChange={event => {
              setFilter(event.currentTarget.value)
            }}
            onKeyDown={keyboardEvent => {
              if (keyboardEvent.key !== 'ArrowDown') return
              const firstItem = firstEnabledItem()
              if (!firstItem) return
              keyboardEvent.preventDefault()
              firstItem.focus()
              firstItem.scrollIntoView({block: 'nearest'})
            }}
            className={styles.Search}
          />
          <div className={styles.List}>
            {scopeOwner === null || scopeOwner === '' ? (
              <p className={styles.Empty}>Resolving repository owner</p>
            ) : (
              <>
                <ActionList
                  ref={listRef}
                  role="menu"
                  selectionVariant="single"
                  aria-label="Select a repository"
                  onKeyDownCapture={keyboardEvent => {
                    if (keyboardEvent.key !== 'ArrowUp' || keyboardEvent.target !== firstEnabledItem()) return
                    keyboardEvent.preventDefault()
                    keyboardEvent.stopPropagation()
                    searchRef.current?.focus()
                  }}
                  onFocusCapture={event => {
                    if (event.target instanceof HTMLElement && event.target.matches('[role^="menuitem"]')) {
                      event.target.scrollIntoView({block: 'nearest'})
                    }
                  }}
                >
                  {!filter && (
                    <ActionList.Group selectionVariant="single" className={styles.ChatGroup}>
                      <ActionList.GroupHeading>
                        {ownerView ? '' : GENERAL_CONVERSATIONS_HEADING}
                      </ActionList.GroupHeading>
                      <ActionList.Item
                        className={styles.ChatRow}
                        role="menuitemradio"
                        aria-checked={chatSelected}
                        selected={chatSelected}
                        onSelect={() => {
                          onSelectChat()
                          close()
                        }}
                        onKeyDown={keyboardEvent => {
                          if (keyboardEvent.key === 'ArrowRight' && chatOwner.renderPanel) {
                            keyboardEvent.preventDefault()
                            showOwner()
                          }
                        }}
                      >
                        <ActionList.LeadingVisual>
                          <MessageCircleIcon />
                        </ActionList.LeadingVisual>
                        Chat
                        <ActionList.Description variant="block">{CHAT_DESCRIPTION}</ActionList.Description>
                      </ActionList.Item>
                      {chatOwner.renderPanel && (
                        <ActionList.Item
                          ref={disclosureRef}
                          role="menuitem"
                          onSelect={showOwner}
                          onKeyDown={keyboardEvent => {
                            if (keyboardEvent.key === 'ArrowRight') {
                              keyboardEvent.preventDefault()
                              showOwner()
                            }
                          }}
                          className={styles.Disclosure}
                        >
                          {chatOwner.avatarUrl && (
                            <GitHubAvatar
                              src={chatOwner.avatarUrl}
                              alt=""
                              size={16}
                              square={chatOwner.kind === 'organization'}
                            />
                          )}
                          {!chatOwner.avatarUrl &&
                            (chatOwner.kind === 'organization' ? (
                              <OrganizationIcon size={16} aria-hidden="true" />
                            ) : (
                              <PersonIcon size={16} aria-hidden="true" />
                            ))}
                          <ChevronRightIcon aria-hidden="true" />
                          <VisuallyHidden>Change Chat scope, current: {chatOwner.accessibleLabel}</VisuallyHidden>
                        </ActionList.Item>
                      )}
                    </ActionList.Group>
                  )}
                  {!filter && (
                    <ActionList.Group selectionVariant="single">
                      <ActionList.GroupHeading>{ownerView ? '' : WORK_IN_REPOSITORY_HEADING}</ActionList.GroupHeading>
                      {repositoryItems}
                      {loading && (
                        <li role="none">
                          <div aria-hidden="true" data-testid="chat-target-repositories-loading">
                            {LOADING_ROWS.map(row => (
                              <div key={row} className={styles.LoadingRow}>
                                <SkeletonBox width="16px" height="16px" />
                                <SkeletonBox width="65%" height="16px" />
                              </div>
                            ))}
                          </div>
                        </li>
                      )}
                    </ActionList.Group>
                  )}
                  {filter && repositoryItems}
                </ActionList>
                {loadFailed && (
                  <div className={styles.Empty}>
                    <p>Could not load repositories</p>
                    <Button
                      type="button"
                      variant="invisible"
                      size="small"
                      onClick={() => {
                        requestAnimationFrame(() => searchRef.current?.focus())
                        retryLoad()
                      }}
                    >
                      Retry
                    </Button>
                  </div>
                )}
                {!loadFailed && ((filter && loading) || (!loading && visibleRepositories.length === 0)) && (
                  <p className={styles.Empty}>{loading ? 'Searching repositories…' : 'No repositories found'}</p>
                )}
              </>
            )}
          </div>
          {secondaryAction && <div className={styles.Footer}>{secondaryAction}</div>}
        </div>
      </div>
    </AnchoredOverlay>
  )
}
